import React from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { DateConstants, IconDefinitionConstants } from "utils/constants";
import { IconProfileInfo } from "models/propTypes/userProfilePropTypes";
import { SharedDocumentListProps } from "models/propTypes/documentPropTypes";
//#endregion
//#region components
import { Icon, NestedTable, RoundButton } from "components/common";
import { useDocumentService } from "serviceHooks";
//#endregion
import { FileHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const SharedDocumentsList: React.FC<SharedDocumentListProps> = (props) => {
  const documentService = useDocumentService();
  const [result, setResult] = React.useState<WintSuperkollAPIModel.SharedDocument[]>([]);

  React.useEffect(() => {
    documentService.getSharedDocuments().then((res) => setResult(res ?? []));
  }, [props.reloadKey]);

  const resolveExportDocumentType = (driveFileItemType: WintSuperkollAPIModel.DriveFileItemType) => {
    switch (driveFileItemType) {
      case WintSuperkollAPIModel.DriveFileItemType.Drawing:
        return WintSuperkollAPIModel.ExportDocumentType.PNG;
      case WintSuperkollAPIModel.DriveFileItemType.Document:
        return WintSuperkollAPIModel.ExportDocumentType.MSWord;
      case WintSuperkollAPIModel.DriveFileItemType.Presentation:
        return WintSuperkollAPIModel.ExportDocumentType.MSPowerPoint;
      case WintSuperkollAPIModel.DriveFileItemType.Sheet:
        return WintSuperkollAPIModel.ExportDocumentType.MSExcel;
      default:
        return undefined;
    }
  };

  const onDownloadClick = (row: WintSuperkollAPIModel.SharedDocument) => {
    if (row.id) {
      const type = resolveExportDocumentType(row.type);
      return documentService.downloadSharedDocument(row.id, type).then((res) => {
        FileHelper.download(res);
      });
    }
  };

  return (
    <NestedTable<WintSuperkollAPIModel.SharedDocument, {}>
      rows={result}
      properties={{
        title: {
          description: TranslationKeys.Common.Name,
          formatter: (row: WintSuperkollAPIModel.SharedDocument, rowIndex: number, depth?: number) =>
            new IconProfileInfo(row.title ?? "", row.iconUrl ?? "", undefined, true),
        },
        updatedDate: {
          description: TranslationKeys.Common.Changed,
          formatter: (row: WintSuperkollAPIModel.SharedDocument) =>
            LocalizationHelperInstance.getFormattedDate(new Date(row.updatedDate), DateConstants.DateTimeConfig),
          width: 200,
          alignment: "left",
        },
        id: {
          description: TranslationKeys.Common.Download,
          formatter: (row: WintSuperkollAPIModel.SharedDocument) =>
            row.type !== WintSuperkollAPIModel.DriveFileItemType.Directory ? (
              <RoundButton className={cssExports.tableIconButton} onClick={onDownloadClick.bind(null, row)}>
                <Icon icon={IconDefinitionConstants.WDownload} />
              </RoundButton>
            ) : undefined,
          alignment: "center",
          width: 20,
        },
      }}
      childrenKey={(parent: WintSuperkollAPIModel.SharedDocument) =>
        documentService.getSharedDocumentsByParent(parent.id ?? "")
      }
      hasChildren={(parent: WintSuperkollAPIModel.SharedDocument) =>
        parent.type === WintSuperkollAPIModel.DriveFileItemType.Directory
      }
      hasMultiLevels
      className={cssExports.sharedDocuments}
    />
  );
};

export default SharedDocumentsList;
